(() => {
  if (window.EpicHomeHero) return;

  const instances = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class Hero {
    constructor(root) {
      this.root = root;
      this.slides = [...root.querySelectorAll('[data-hero-slide]')];
      this.dots = [...root.querySelectorAll('[data-hero-dot]')];
      this.toggle = root.querySelector('[data-hero-play]');
      this.index = 0;
      this.userPaused = false;
      this.manualPlay = false;
      this.visible = false;
      this.hasShown = false;
      this.transitionFrame = null;
      this.transitionTimer = null;
      this.requestId = 0;
      this.gesture = null;
      this.controller = new AbortController();
      const { signal } = this.controller;
      const stage = root.querySelector('.epic-home-hero__stage');

      root.querySelector('[data-hero-prev]')?.addEventListener('click', () => this.show(this.index - 1), { signal });
      root.querySelector('[data-hero-next]')?.addEventListener('click', () => this.show(this.index + 1), { signal });
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.show(index), { signal }));
      this.toggle?.addEventListener('click', () => this.toggleVideo(), { signal });
      stage?.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
      stage?.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal });
      stage?.addEventListener('pointercancel', () => this.cancelGesture(), { signal });
      stage?.addEventListener('lostpointercapture', () => this.cancelGesture(), { signal });
      stage?.addEventListener('dragstart', (event) => {
        if (event.target.closest('.epic-home-hero__poster, .epic-home-hero__video')) event.preventDefault();
      }, { signal });
      this.slides.forEach((slide) => {
        const video = slide.querySelector('[data-hero-video]');
        video?.addEventListener('play', () => this.updateToggle(), { signal });
        video?.addEventListener('pause', () => this.updateToggle(), { signal });
      });
      root.addEventListener('pointerenter', () => this.stopTimer(), { signal });
      root.addEventListener('pointerleave', () => this.startTimer(), { signal });
      root.addEventListener('focusin', () => this.stopTimer(), { signal });
      root.addEventListener('focusout', (event) => {
        if (!root.contains(event.relatedTarget)) this.startTimer();
      }, { signal });
      document.addEventListener('visibilitychange', () => this.sync(), { signal });
      reducedMotion.addEventListener('change', () => this.sync(), { signal });
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        if (this.visible) this.warmNextImage();
        this.sync();
      }, { threshold: 0.15 });
      this.observer.observe(root);
      root.classList.add('is-ready');
      this.show(0);
    }

    currentVideo() {
      return this.slides[this.index]?.querySelector('[data-hero-video]');
    }

    onPointerDown(event) {
      if (this.slides.length < 2 || !event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
      if (event.target.closest('a, button, input, select, textarea, [contenteditable]')) return;
      const stage = event.currentTarget;
      this.gesture = { id: event.pointerId, x: event.clientX, y: event.clientY };
      stage.setPointerCapture(event.pointerId);
      stage.classList.add('is-dragging');
      this.stopTimer();
    }

    onPointerUp(event) {
      if (!this.gesture || event.pointerId !== this.gesture.id) return;
      const distanceX = event.clientX - this.gesture.x;
      const distanceY = event.clientY - this.gesture.y;
      this.cancelGesture();
      if (Math.abs(distanceX) >= 48 && Math.abs(distanceX) > Math.abs(distanceY) * 1.2) {
        this.show(this.index + (distanceX < 0 ? 1 : -1));
      } else {
        this.startTimer();
      }
    }

    cancelGesture(restartTimer = true) {
      if (!this.gesture) return;
      const stage = this.root.querySelector('.epic-home-hero__stage');
      const pointerId = this.gesture.id;
      this.gesture = null;
      stage?.classList.remove('is-dragging');
      if (stage?.hasPointerCapture(pointerId)) stage.releasePointerCapture(pointerId);
      if (restartTimer) this.startTimer();
    }

    warmNextImage() {
      if (this.slides.length < 2) return;
      const image = this.slides[(this.index + 1) % this.slides.length].querySelector('img');
      if (image) image.loading = 'eager';
    }

    loadVideo(video) {
      if (!video || video.dataset.loaded) return;
      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      video.dataset.loaded = 'true';
      video.load();
    }

    sync() {
      const video = this.currentVideo();
      const canPlay = this.visible && !document.hidden && (!reducedMotion.matches || this.manualPlay) && !this.userPaused;
      this.slides.forEach((slide, index) => {
        if (index !== this.index) slide.querySelector('video')?.pause();
      });
      if (video) {
        if (canPlay) {
          this.loadVideo(video);
          video.play().catch(() => {
            this.userPaused = true;
            this.updateToggle();
          });
        } else {
          video.pause();
        }
      }
      this.updateToggle();
      if (canPlay && this.root.dataset.autoplay === 'true' && this.slides.length > 1 && !this.root.matches(':hover') && !this.root.contains(document.activeElement)) {
        this.startTimer();
      } else {
        this.stopTimer();
      }
    }

    show(index, imageReady = false) {
      if (!this.slides.length) return;
      this.stopTimer();
      const requestId = ++this.requestId;
      const nextIndex = (index + this.slides.length) % this.slides.length;
      const next = this.slides[nextIndex];
      const nextImage = next.querySelector('img');
      if (nextImage) nextImage.loading = 'eager';
      if (this.hasShown && nextIndex !== this.index && nextImage && !imageReady) {
        nextImage.decode().catch(() => {}).then(() => {
          if (requestId === this.requestId) this.show(nextIndex, true);
        });
        return;
      }
      this.currentVideo()?.pause();
      window.cancelAnimationFrame(this.transitionFrame);
      window.clearTimeout(this.transitionTimer);
      const previous = this.slides[this.index];
      const animate = this.hasShown && nextIndex !== this.index && !reducedMotion.matches && this.visible;
      this.slides.forEach((slide, slideIndex) => {
        slide.classList.remove('is-entering', 'is-leaving');
        slide.hidden = slideIndex !== this.index;
      });
      this.index = nextIndex;
      if (animate) {
        previous.hidden = false;
        previous.inert = true;
        previous.classList.add('is-leaving');
        next.hidden = false;
        next.inert = false;
        next.classList.add('is-entering');
        this.transitionFrame = window.requestAnimationFrame(() => {
          this.transitionFrame = window.requestAnimationFrame(() => next.classList.remove('is-entering'));
        });
        this.transitionTimer = window.setTimeout(() => {
          previous.hidden = true;
          previous.classList.remove('is-leaving');
          this.transitionTimer = null;
        }, 270);
      } else {
        this.slides.forEach((slide, slideIndex) => {
          slide.hidden = slideIndex !== this.index;
          slide.inert = slideIndex !== this.index;
        });
      }
      this.hasShown = true;
      const activeImage = this.slides[this.index].querySelector('img');
      if (activeImage && this.visible) activeImage.loading = 'eager';
      if (this.visible) this.warmNextImage();
      this.dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', String(dotIndex === this.index)));
      this.userPaused = false;
      this.manualPlay = false;
      if (this.toggle) this.toggle.hidden = !this.currentVideo();
      this.sync();
    }

    toggleVideo() {
      const video = this.currentVideo();
      if (!video) return;
      if (video.paused) {
        this.userPaused = false;
        this.manualPlay = true;
        this.loadVideo(video);
        video.play().catch(() => {
          this.userPaused = true;
          this.updateToggle();
        });
      } else {
        this.userPaused = true;
        this.manualPlay = false;
        video.pause();
      }
      this.sync();
    }

    updateToggle() {
      if (!this.toggle) return;
      const paused = this.userPaused || !this.currentVideo() || this.currentVideo().paused;
      this.toggle.setAttribute('aria-pressed', String(paused));
      this.toggle.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
    }

    startTimer() {
      if (this.timer || this.gesture || !this.visible || document.hidden || reducedMotion.matches || this.userPaused || this.root.dataset.autoplay !== 'true' || this.root.matches(':hover') || this.root.contains(document.activeElement) || this.slides.length < 2) return;
      const interval = Math.max(5000, Number(this.root.dataset.interval) || 7000);
      this.timer = window.setInterval(() => this.show(this.index + 1), interval);
    }

    stopTimer() {
      window.clearInterval(this.timer);
      this.timer = null;
    }

    destroy() {
      this.stopTimer();
      this.cancelGesture(false);
      this.requestId += 1;
      window.cancelAnimationFrame(this.transitionFrame);
      window.clearTimeout(this.transitionTimer);
      this.observer.disconnect();
      this.controller.abort();
      this.root.classList.remove('is-ready');
      this.slides.forEach((slide) => slide.querySelector('video')?.pause());
    }
  }

  function mount(scope = document) {
    const roots = scope.matches?.('[data-epic-home-hero]') ? [scope] : scope.querySelectorAll('[data-epic-home-hero]');
    roots.forEach((root) => {
      if (!instances.has(root)) instances.set(root, new Hero(root));
    });
  }

  function unmount(scope) {
    const roots = scope.matches?.('[data-epic-home-hero]') ? [scope] : scope.querySelectorAll('[data-epic-home-hero]');
    roots.forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
  }

  window.EpicHomeHero = { mount, unmount };
  mount();
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => unmount(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-hero-slide]');
    const root = event.target.closest('[data-epic-home-hero]');
    if (slide && root && instances.has(root)) instances.get(root).show(instances.get(root).slides.indexOf(slide));
  });
})();
